import * as Sentry from '@sentry/react';
import {getSentryErrorSampleRate} from './sampling';

/**
 * 精简版 Sentry 初始化配置
 * 只专注于错误上报功能
 */
export function initSentry(dsn: string, environment: string = 'development') {
  console.log('🚀 开始初始化 Sentry...', {dsn, environment});

  Sentry.init({
    dsn,
    environment,
    debug: false, // 关闭 Sentry 调试日志
    // 精简版：只保留核心错误监控功能
    integrations: [
      // 自动捕获未处理的 Promise 错误
      Sentry.browserTracingIntegration(),
    ],
    sampleRate: getSentryErrorSampleRate(),
    // 设置采样率 - 生产环境建议降低
    tracesSampleRate: __DEV__ ? 0.1 : 0.01, // 降低采样率减少日志

    // 错误发送前的处理
    beforeSend(event) {
      console.log('📤 Sentry 准备发送事件:', event);

      // 过滤掉一些不重要的错误
      if (event.exception) {
        const error = event.exception.values?.[0];
        if (error?.type === 'ChunkLoadError') {
          console.log('🚫 过滤掉 ChunkLoadError');
          return null; // 忽略 chunk 加载错误
        }
      }
      return event;
    },
  });

  console.log('✅ Sentry 初始化完成！');
}

/**
 * 设置用户信息
 */
export function setSentryUser(user: {
  id: string;
  username?: string;
  email?: string;
}) {
  Sentry.setUser(user);
}

/**
 * 手动捕获错误
 */
export function captureError(error: Error, context?: any) {
  console.log('🚨 手动上报错误到 Sentry:', {
    error: error.message,
    stack: error.stack,
    context,
  });

  Sentry.captureException(error, {
    tags: {
      component: context?.component || 'unknown',
    },
    extra: context,
  });

  console.log('✅ 错误已上报到 Sentry');
}

/**
 * 手动捕获消息
 */
export function captureMessage(
  message: string,
  level: 'info' | 'warning' | 'error' | 'debug' = 'info',
) {
  console.log('📝 手动上报消息到 Sentry:', {
    message,
    level,
  });

  Sentry.captureMessage(message, level);

  console.log('✅ 消息已上报到 Sentry');
}

/**
 * 设置标签
 */
export function setSentryTag(key: string, value: string) {
  Sentry.setTag(key, value);
}

/**
 * 设置上下文
 */
export function setSentryContext(key: string, context: any) {
  Sentry.setContext(key, context);
}

type SentryEventType = 'exception' | 'message';

interface SentryEventOptions {
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
  contexts?: Record<string, Record<string, unknown>>;
  user?: {
    id?: string | number;
    email?: string;
    ip_address?: string;
    username?: string;
    [key: string]: unknown;
  };
  fingerprint?: string[];
  level?: 'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug';
}

/**
 * 上报自定义事件到 Sentry，并为本次事件设置标签
 */
export function reportSentryEvent(
  type: SentryEventType,
  payload: Error | string,
  options?: SentryEventOptions,
) {
  if (options?.tags) {
    Sentry.withScope(scope => {
      Object.entries(options.tags!).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
      if (type === 'exception') {
        Sentry.captureException(payload, options);
      } else if (type === 'message') {
        Sentry.captureMessage(payload as string, options);
      }
    });
  } else {
    if (type === 'exception') {
      Sentry.captureException(payload, options);
    } else if (type === 'message') {
      Sentry.captureMessage(payload as string, options);
    }
  }
}
